import {
    TextField, Button, Box, Typography, Link, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow,
    Dialog, DialogContent, IconButton, Stack, Toolbar
} from "@mui/material";
import { useState, useEffect } from "react";
import { Close, Description, Info } from "@mui/icons-material";
import { getCorporations, importEmployees } from "../../api";
import PayrollsPopup from "../../Components/PayrollPopup";
import XLSX from "xlsx-js-style";
import SampleTemplateFile from "../../files/Employee Import.xlsx";
import { validateAndParseEmployeeFile, normalizeErrorRows } from "../../Validations/EmployeeImportValidation";
import { Dropdown } from "../../HelperFunctions/PayrollDropdown";
import { useParams } from "react-router-dom";
import { Loader } from "../../Components/Loader";
import { enqueueSnackbar } from "notistack";
import { employeeManagementStyles } from "../../styles/employeeManagement.styles";

// --- Constants & Styles ---
const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_EXTENSIONS = [".xlsx", ".csv", ".xls"];
const SPECIAL_CHAR_REGEX = /[^a-zA-Z0-9.\-_ ()]/g;

const DROPDOWN_SX = {
    maxWidth: "250px",
    "& .MuiFilledInput-root": {
        maxHeight: 32,
        backgroundColor: "white",
        "&:hover": { backgroundColor: "white" },
        "&.Mui-focused": { backgroundColor: "white" },
        "&.Mui-disabled": { backgroundColor: "#f5f5f5" }
    },
    "& .MuiFilledInput-input": {
        paddingTop: "6px",
        paddingBottom: "6px",
    },
};

const FILE_INPUT_SX = {
    width: "250px",
    "& .MuiFilledInput-root": {
        height: 32,
        backgroundColor: "white",
        border: "1px solid #ccc",
        borderRadius: 1,
        "&:hover": { backgroundColor: "white" },
        "&.Mui-focused": { backgroundColor: "white" },
        paddingRight: 0,
        display: "flex",
        alignItems: "center",
    },
    "& .MuiFilledInput-input": { padding: "0 12px" },
    "& .MuiFilledInput-underline:before": { display: "none" },
    "& .MuiFilledInput-underline:after": { display: "none" },
};

// --- Validation & Formatting Helpers ---
const validateSelectedFile = (file) => {
    if (file.size > MAX_FILE_SIZE_BYTES) {
        return `File exceeds maximum size of ${MAX_FILE_SIZE_MB}MB.`;
    }
    if (SPECIAL_CHAR_REGEX.test(file.name)) {
        return "File name cannot contain special characters.";
    }
    const fileName = file.name.trim().toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf("."));
    if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
        return "Invalid file type. Only .xls, or .xlsx files are allowed.";
    }
    return null;
};

const formatImportErrors = (response) => {
    const invalidData = response?.InvalidDataResponse;
    const errorList = response?.ErrorList || response?.errorList;
    const formattedErrors = [];

    if (invalidData && Object.keys(invalidData).length > 0) {
        Object.entries(invalidData).forEach(([identifier, errors]) => {
            if (Array.isArray(errors)) {
                errors.forEach(errorMsg => {
                    // The identifier might be the name or the row number.
                    formattedErrors.push({ Row: identifier, Error: errorMsg });
                });
            }
        });
    } else if (errorList && errorList.length > 0) {
        formattedErrors.push(...normalizeErrorRows(errorList));
    }

    return formattedErrors;
};

const convertPayloadValuesToStrings = (data) => {
    if (data === null || typeof data === 'undefined') {
        return data;
    }

    if (Array.isArray(data)) {
        return data.map(item => convertPayloadValuesToStrings(item));
    }

    if (typeof data === 'object' && !(data instanceof Date)) {
        const newObject = {};
        for (const key in data) {
            if (Object.prototype.hasOwnProperty.call(data, key)) {
                newObject[key] = convertPayloadValuesToStrings(data[key]);
            }
        }
        return newObject;
    }

    return String(data);
};

const EmployeeImport = ({ open, onClose, onSuccess, token }) => {
    const { corpId } = useParams();

    const [corporations, setCorporations] = useState([]);
    const [selectedCorp, setSelectedCorp] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileObj, setFileObj] = useState(null);
    const [validationLoading, setValidationLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [errorRows, setErrorRows] = useState([]);
    const [uploadedData, setUploadedData] = useState([]);
    const [, setUploadedDataHeaders] = useState([]);

    const isLoading = validationLoading || actionLoading;

    // --- CONFIRMATION STATE ---
    const [alertPopup, setAlertPopup] = useState({
        open: false,
        type: "",
        message: "",
        onConfirm: null
    });

    // --- Initialization Effects ---
    useEffect(() => {
        if (!open) return;

        const fetchInitialData = async () => {
            setValidationLoading(true);
            try {
                const corpsData = await getCorporations(token);
                
                const formatted = corpsData.map(c => ({
                    corpName: c.Name,
                    corpValue: c.ID
                }));
                setCorporations([{ corpName: "--Select--", corpValue: "000000000000000000000000000000000000" }, ...formatted]);
            } catch (error) {
                console.error("Error fetching initial data:", error);
            } finally {
                setValidationLoading(false);
            }
        };
        
        fetchInitialData();
    }, [open, token]);

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const urlCorpId = corpId || searchParams.get("corpid") || searchParams.get("corpId") || searchParams.get("corpselect");
        
        if (urlCorpId && corporations && corporations.length > 0 && !selectedCorp) {
            const matchedCorp = corporations.find(c => String(c.corpValue) === String(urlCorpId));
            if (matchedCorp) {
                setSelectedCorp(matchedCorp);
            }
        }
    }, [corporations, selectedCorp, corpId]);

    // --- Event Handlers ---
    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const validationError = validateSelectedFile(file);
        if (validationError) {
            setAlertPopup({
                open: true,
                type: "error",
                message: validationError,
                onConfirm: () => setAlertPopup({ ...alertPopup, open: false })
            });
            event.target.value = "";
            setSelectedFile(null);
            setFileObj(null);
            return;
        }

        setSelectedFile(file.name);
        setFileObj(file);
    };

    const exportErrorsToCSV = () => {
    //     console.log("uploadedData:", uploadedData);
    // console.log("errorRows:", errorRows);
    if (uploadedData.length === 0) return;

    // Group errors by row/employee
    const errorsByIdentifier = errorRows.reduce((acc, { Row, Error }) => {
        const identifier = String(Row).trim();

        if (!acc[identifier]) {
            acc[identifier] = [];
        }

        acc[identifier].push(Error);
        return acc;
    }, {});

    const dataToExport = uploadedData.map((row) => {
        const identifierByRow = String(row.__rowNum__);
        const identifierByName = `${row["First Name"] || ""} ${row["Middle Name"] || ""} ${row["Last Name"] || ""}`.trim();

        const errors =
            errorsByIdentifier[identifierByRow] ||
            errorsByIdentifier[identifierByName] ||
            [];

        const { __rowNum__, ...originalData } = row;

        return {
            ...originalData,
            Errors: errors.join("\n")
        };
    });

        const ws = XLSX.utils.json_to_sheet(dataToExport);

        // Auto-size columns
        const keys = Object.keys(dataToExport[0] || {});
        ws["!cols"] = keys.map(key => {
            const maxLength = Math.max(
                key.length,
                ...dataToExport.map(r => r[key] ? String(r[key]).length : 0)
            );
            return { wch: Math.min(maxLength + 3, 50) };
        });

        // Header and Body styling
        const range = XLSX.utils.decode_range(ws["!ref"]);
        for (let R = range.s.r; R <= range.e.r; ++R) {
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const cell_address = { c: C, r: R };
                const cell_ref = XLSX.utils.encode_cell(cell_address);
                if (!ws[cell_ref]) continue;

                if (R === 0) { // Header row
                    ws[cell_ref].s = {
                        font: { name: "Poppins", bold: true, sz: 11, color: { rgb: "000000" } },
                        fill: { fgColor: { rgb: "F5F5F5" } },
                        alignment: { horizontal: "center", vertical: "center" }
                    };
                } else { // Body rows
                    ws[cell_ref].s = {
                        font: { name: "Poppins", sz: 11 },
                        alignment: { vertical: "center" }
                    };
                }
            }
        }

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Employee Import");
        XLSX.writeFile(wb, "EmployeeImportResult.xlsx");
    };

    const handleImportClick = async () => {
        const corpIdVal = selectedCorp?.corpValue || selectedCorp?.ID || selectedCorp?.value;
        if (!selectedCorp || !corpIdVal || corpIdVal === "000000000000000000000000000000000000") {
            enqueueSnackbar("Please select a Corporation to import file.", { variant: "error" });
            return;
        }

        if (!fileObj) {
            enqueueSnackbar("Please choose a file to import.", { variant: "error" });
            return;
        }

        setValidationLoading(true);

        setErrorRows([]);
        setUploadedData([]);
        setUploadedDataHeaders([]);

        // The validator historically expected these arrays for specific internal business rules.
        // Supplying empty arrays as placeholders for disabled validations while preserving signature.
        const emptyPlaceholder = []; 
        const result = await validateAndParseEmployeeFile(fileObj, selectedCorp, emptyPlaceholder, emptyPlaceholder, emptyPlaceholder, emptyPlaceholder, emptyPlaceholder);
        // console.log("RESULT:", result);
        if (result.error) {
            if (Array.isArray(result.allRows) && result.allRows.length > 0) {
                setUploadedData(result.allRows);
                setUploadedDataHeaders(Object.keys(result.allRows[0]));
            }
        }

        if (result.error) {
            setValidationLoading(false);
            setAlertPopup({
                open: true,
                type: "error",
                message: result.error, // This was the line causing the issue
                onConfirm: () => setAlertPopup({ ...alertPopup, open: false })
            });
            return;
        }

        if (result.errorRows) {
            setErrorRows(result.errorRows);
            setValidationLoading(false);
            setAlertPopup({
                open: true,
                type: "error",
                message: `Found ${result.errorRows.length} error(s) in the file. Please check the error list and export the failed records for correction.`,
                onConfirm: () => setAlertPopup({ ...alertPopup, open: false })
            });
            return;
        }

        setValidationLoading(false);
        if (Array.isArray(result.allRows) && result.allRows.length > 0) {
            setUploadedData(result.allRows);
            setUploadedDataHeaders(Object.keys(result.allRows[0]));
        }

        if (result.payload) {
            try {
                setActionLoading(true);
                // Convert all values in the payload to strings before sending
                const stringifiedPayload = convertPayloadValuesToStrings(result.payload);
                const response = await importEmployees(stringifiedPayload, token);

                const total = response.TotalRecords ?? response.totalRecords ?? (Array.isArray(uploadedData) && uploadedData.length > 0 ? uploadedData.length : (Array.isArray(result.allRows) ? result.allRows.length : 0));

                if (response?.Status === "Success" || response?.StatusCode === 200 || response?.Status?.toLowerCase().includes("successfully")) {
                    const formattedErrors = formatImportErrors(response);

                    if (formattedErrors.length > 0) {
                        setErrorRows(formattedErrors);
                        const failed = formattedErrors.length;
                        const imported = response.ImportedRecords ?? response.importedRecords ?? Math.max(0, total - failed);
                        setAlertPopup({
                            open: true,
                            type: "error",
                            message: `Total Records: ${total}, Imported: ${imported}, Failed: ${failed}. Please check the error list and export the failed records for correction.`,
                            onConfirm: () => setAlertPopup(prev => ({ ...prev, open: false }))
                        });
                        if (imported > 0 && onSuccess) onSuccess();
                    } else {
                        const imported = response.ImportedRecords ?? response.importedRecords ?? total;
                        setAlertPopup({
                            open: true,
                            type: "success",
                            message: `Total Records: ${total}, Imported: ${imported}.`,
                            onConfirm: () => {
                                setAlertPopup(prev => ({ ...prev, open: false }));
                                if (onSuccess) onSuccess();
                                onClose();
                            }
                        });
                    }
                } else {
                    const invalidData = response?.InvalidDataResponse;
                    const errorList = response?.ErrorList || response?.errorList;

                    if (invalidData && Object.keys(invalidData).length > 0) {
                        const errs = formatImportErrors(response);
                        setErrorRows(errs);
                        const failed = errs.length;
                        const imported = Math.max(0, total - failed);
                        setAlertPopup({
                            open: true,
                            type: "error",
                            message: `Total Records: ${total}, Imported: ${imported}, Failed: ${failed}. Please check the error list and export the failed records for correction.`,
                            onConfirm: () => setAlertPopup(prev => ({ ...prev, open: false }))
                        });
                    } else if (errorList && errorList.length > 0) {
                        const errs = normalizeErrorRows(errorList);
                        setErrorRows(errs);
                        const failed = errs.length;
                        const imported = Math.max(0, total - failed);
                        setAlertPopup({
                            open: true,
                            type: "error",
                            message: `Total Records: ${total}, Imported: ${imported}, Failed: ${failed}. Please check the error list and export the failed records for correction.`,
                            onConfirm: () => setAlertPopup(prev => ({ ...prev, open: false }))
                        });
                    } else {
                        const msg = response?.Message || response?.title || response?.Status || (typeof response === 'string' ? response : `Total Records: ${total}, Imported: 0, Failed: ${total}. Please check the file and try again.`);
                        setAlertPopup({
                            open: true,
                            type: "error",
                            message: msg,
                            onConfirm: () => setAlertPopup(prev => ({ ...prev, open: false }))
                        });
                    }
                }
            } catch (error) {
                if (error.response && error.response.status === 400) {
                    enqueueSnackbar("Column headers do not match the template. Please use the correct format.", { variant: "error" });
                } else {
                    enqueueSnackbar(error.message || "An unexpected error occurred.", { variant: "error" });
                }
            } finally {
                setActionLoading(false);
            }
        }
    };

    const groupedErrorRows = Object.values(
        errorRows.reduce((acc, { Row, Error }) => {
            if (!acc[Row]) {
                acc[Row] = {
                    Row,
                    Errors: [],
                };
            }
            acc[Row].Errors.push(Error);
            return acc;
        }, {})
    );

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="lg"
            sx={employeeManagementStyles.importDialog}
        >
            <Toolbar sx={employeeManagementStyles.importToolbar}>
                <Typography sx={employeeManagementStyles.importTitle} component="div" fontWeight={600} fontSize={"16px"}>
                    Employee Import
                </Typography>

                {errorRows.length > 0 && (
                    <Button
                        size="small"
                        variant="outlined"
                        disableElevation
                        sx={employeeManagementStyles.exportErrorsBtn}
                        onClick={exportErrorsToCSV}
                    >
                        Export Errors
                    </Button>
                )}
                <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    sx={employeeManagementStyles.importSubmitBtn}
                    onClick={handleImportClick}
                    disabled={isLoading}
                >
                    Import
                </Button>

                <IconButton
                    size="small"
                    aria-label="Close"
                    onClick={onClose}
                    style={{ backgroundColor: "#686868", borderRadius: 2, width: 20, height: 20, marginLeft: 8 }}
                    disableRipple
                >
                    <Close sx={employeeManagementStyles.importCloseIcon} />
                </IconButton>
            </Toolbar>

            <DialogContent sx={employeeManagementStyles.importDialogContent}>
                {isLoading ? (<Loader loading={isLoading} />
                ) : (
                    <Stack gap={3} height="100%">
                        <Box>
                            <Typography color="text.secondary" fontSize={11} fontWeight={500} mb={0.5}>Corporation</Typography>
                            <Dropdown
                                label=""
                                name="corporation"
                                value={selectedCorp}
                                loading={validationLoading}
                                options={corporations}
                                disabled={isLoading}
                                getOptionLabel={(option) => option?.corpName || ""}
                                isEqual={(option, value) => String(option?.corpValue) === String(value?.corpValue)}
                                onChange={(name, newValue) => setSelectedCorp(newValue || null)}
                                sx={DROPDOWN_SX}
                                inputProps={{ style: { paddingLeft: 12, paddingRight: 12 } }}
                            />
                        </Box>

                        <Box>
                            <Typography color="text.secondary" fontSize={12} fontWeight={500} mb={0.5}>File Upload</Typography>
                            <TextField
                                helperText="File types Supported :  .xlxs ,.csv ,.xls"
                                size="small"
                                variant="filled"
                                value={selectedFile || "No file selected"}
                                FormHelperTextProps={{
                                    sx: {
                                        color: "primary.main",
                                        m: 0,
                                        ml: 1
                                    }
                                }}
                                inputProps={{ style: { fontSize: 11 } }}
                                InputProps={{
                                    readOnly: true,
                                    sx: { paddingRight: 0 },
                                    endAdornment: (
                                        <Button
                                            component="label"
                                            size="small"
                                            sx={{
                                                borderTopLeftRadius: 0,
                                                borderBottomLeftRadius: 0,
                                                height: '100%'
                                            }}
                                        >
                                            Browse
                                            <input
                                                type="file"
                                                hidden
                                                accept=".xlsx,.csv,.xls"
                                                onChange={handleFileChange}
                                            />
                                        </Button>
                                    ),
                                }}
                                sx={FILE_INPUT_SX}
                            />
                        </Box>

                        {errorRows.length > 0 ? (
                            <>
                            <Box flexGrow={1} overflow="auto">
                                <Typography color="error" fontSize={15} fontWeight={500} mb={1}>Error List</Typography>
                                <TableContainer sx={employeeManagementStyles.importTableContainer}>
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow sx={employeeManagementStyles.importTableRowHeader}>
                                                <TableCell sx={{ fontSize: 12 }}>Row / Employee</TableCell>
                                                <TableCell sx={{ fontSize: 12 }}>Errors</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {groupedErrorRows.map((row, index) => (
                                                <TableRow key={index}>
                                                    <TableCell sx={{ fontSize: 11 }}>{row["Row"] || "N/A"}</TableCell>
                                                    <TableCell sx={{ color: 'red', fontSize: 11, whiteSpace: 'pre-wrap' }}>{row["Errors"].join('\n') || ""}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                            </>
                        ) : (
                            <Stack gap={2} mt={5}>
                                <Stack direction={"row"} alignItems={"center"} gap={1}>
                                    <Info sx={{ color: "primary.main", }} />
                                    <Typography color="text.secondary" fontSize={12} fontWeight={500}>
                                        Click on the link to download sample template
                                    </Typography>
                                </Stack>

                                <Stack direction={"row"} alignItems={"center"} gap={1}>
                                    <Description sx={{ color: "primary.main", }} />
                                    <Link href={SampleTemplateFile}
                                        download="Employee Import Template.xlsx"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Download Employee Import Template"
                                    >
                                        Employee Import Template.xlsx
                                    </Link>
                                </Stack>
                            </Stack>
                        )}
                    </Stack>
                )}
            </DialogContent>
            <PayrollsPopup open={alertPopup.open} popupType={alertPopup.type} message={alertPopup.message} onConfirm={alertPopup.onConfirm} onCancel={() => setAlertPopup({ ...alertPopup, open: false })} />
        </Dialog>
    );
};

export default EmployeeImport;
